import './Main.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { WizardData } from './Model'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { TimeDHM } from '@/components/Elements/time'
import { secondsToDHM, toLocalISOString } from '@/common/time'
import { MachineName, MachineNameData } from '@/components/machine'
import { fetchExcel, fetchPDF } from '@/common/fetch'
import { Order, OrderManager } from '@/components/order/Order'
import { TimeZoneNotice } from '@/components/timezone/TimeZoneNotice'

export interface MachineWorkingDays {
  machine: MachineNameData & { id: string };
  day: string // ISO date string (e.g. "2025-12-06")
  engineHours: number
  activeTime: number
  onFireTime: number
}

const MachineWorkingDaysOrderCols = [
  'machine.internalNumber',
  'day',
  'engineHours',
  'activeTime',
  'onFireTime'
]

const MachineWorkingDaysDefaultOrder: Order = {
  key: 'day',
  asc: false
}

export const MachineWorkingDaysReportWizardResult = () => {
  const { services, session, translates } = useAppContext()
  const { wizardData } = useWizard<WizardData>()
  const [data, setData] = useState<MachineWorkingDays[]>()
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 30
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    MachineWorkingDaysOrderCols,
    MachineWorkingDaysDefaultOrder,
    (order: Order) => {
      setOrderManager(current => current.newManager(order))
      setPagginationState(current => ({ ...current, pageNumber: 1 }))
    }
  ))

  function refresh() {
    const query = `from=${encodeURIComponent(toLocalISOString(wizardData.from))}&to=${encodeURIComponent(toLocalISOString(wizardData.to))}`
    services.session.fetchWithToken(`/api/communal/my/reports/machineworkingdays?${query}`).then((response) => {
      return response.json()
    }).then((data: MachineWorkingDays[]) => {
      setData(data)
    })
  }
    
  function loadPDF() {
    const query = [
      `from=${encodeURIComponent(toLocalISOString(wizardData.from))}`,
      `to=${encodeURIComponent(toLocalISOString(wizardData.to))}`,
      `lang=${encodeURIComponent(session?.user.lang || 'en')}`
    ].join('&')
    fetchPDF({
      url: `/api/communal/my/reports/machineworkingdays/pdf?${query}`,
      fileName: "machineworkingdays.pdf",
      services
    })
  }

  function loadExcel() {
    const query = [`from=${encodeURIComponent(toLocalISOString(wizardData.from))}`, `to=${encodeURIComponent(toLocalISOString(wizardData.to))}`, `lang=${encodeURIComponent(session?.user.lang || 'en')}`].join('&')
    fetchExcel({ url: `/api/communal/my/reports/machineworkingdays/excel?${query}`, fileName: 'machine-working-days.xlsx', services })
  }

  useEffect(refresh, [])

  const orderedData = useMemo(() => {
    return orderManager.orderListByKey(data || [])
  }, [data, orderManager])

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }

  function headerRow(key: string, transKey?: string) {
    if (translates === undefined || wizardData === undefined) {
      return null
    }
    transKey = transKey ? transKey : key
    return <div className='hcol' onClick={() => orderManager.click(key)}>
      {translates.for('report.machineworkingdays.summary.table.' + transKey)}
      {orderManager.orderInfoElement(key)}
    </div>
  }

  const renderTable = (
    data: MachineWorkingDays[],
    state: StaticPaginatedTableState,
    setPagginationState: React.Dispatch<React.SetStateAction<StaticPaginatedTableState>> | undefined
  ) => {
    return <StaticPaginatedTable<MachineWorkingDays>
      data={data}
      parentState={state}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('machine.internalNumber', 'machinename')}
        {headerRow('day', 'day')}
        {headerRow('engineHours', 'engine_hours')}
        {headerRow('activeTime', 'active_time')}
        {headerRow('onFireTime', 'on_fire_time')}
      </>}
      row={(row) => {
        const openDetails = () => {
          const url = `${window.location.origin}/communal/statistics/machines?machine=${row.machine.id}`;
          window.open(url, '_blank');
        }
        return <>
          <div className={`col`} onClick={openDetails}>
            <MachineName row={row.machine} />
          </div>
          <div className={`col`}>
            <DateLink machineId={row.machine.id} date={row.day} />
          </div>
          <div className='col numeric-col'>{(row.engineHours ?? 0).toFixed(2)} h</div>
          <div className='col'><TimeDHM dhm={secondsToDHM(row.activeTime ?? 0)} translates={translates} /></div>
          <div className='col'><TimeDHM dhm={secondsToDHM(row.onFireTime ?? 0)} translates={translates} /></div>
        </>
      }}
      translates={translates}
    />
  }

  return <>
    <div>
      <h2>{translates.for('report.machineworkingdays.summary.title')}</h2>
      <TimeZoneNotice />
      {renderTable(orderedData, pagginationState, setPagginationState)}

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={loadExcel}>{translates.for('popup.export.excel_export')}</button>
        <button type="button" className='btn btn-next' onClick={loadPDF}>{translates.for('report.usersranking.wizard.gen_pdf')}</button>
      </div>
    </div>
  </>
}

const DateLink: React.FC<{
  machineId: string
  date: string
}> = (props) => {
  const handleClick = (dateStr: string) => {
    const end = new Date(dateStr)
    end.setHours(23, 59, 59, 999)

    const url = `${window.location.origin}/communal/statistics/machines?length=1d&machine=${props.machineId}&for=${end.toISOString()}`
    window.open(url, '_blank')
  };

  return (
    <span className='day-link' onClick={() => handleClick(props.date)}>
      {props.date}
    </span>
  )
}
